import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

export interface User {
  Id: number;
  Name: string;
  IsUsuarioEmpresa: boolean;
  IsUsuarioEmpresaGestora: boolean;
  Empresa: any | null;
  IsUsuarioAdmin: boolean;
  IsUsuarioValorSaude: boolean;
  IsUsuarioCliente: boolean;
}

export interface UserToken {
  User: User;
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/sid': string;
  nbf: number;
  exp: number;
  iat: number;
  iss: string;
  aud: string;
  token: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly STORAGE_KEY = 'user_token';
  lastUrl: string;

  constructor(private router: Router) { }

  /**
   * Salva os dados do usuário no localStorage
   * @param userData Dados do usuário a serem salvos
   */
  saveUserData(userData: UserToken): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(userData));
    } catch (error) {
      console.error('Erro ao salvar dados do usuário:', error);
    }
  }

  /**
   * Recupera os dados do usuário do localStorage
   * @returns Dados do usuário ou null se não existir
   */
  getUserData(): UserToken | null {
    try {
      const userData = localStorage.getItem(this.STORAGE_KEY);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Erro ao recuperar dados do usuário:', error);
      return null;
    }
  }

  /**
   * Recupera apenas os dados do usuário
   * @returns Dados do usuário ou null se não existir
   */
  getUser(): User | null {
    const userData = this.getUserData();
    return userData ? userData.User : null;
  }

  /**
   * Remove os dados do usuário do localStorage
   */
  clearUserData(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Erro ao remover dados do usuário:', error);
    }
  }

  handleLogin(path: string = this.lastUrl) {
    this.router.navigate(['\site'])
  }

  /**
   * Verifica se existe um usuário logado
   * @returns boolean indicando se existe um usuário logado
   */
  isLoggedIn(): boolean {
    return this.getUserData() !== null;
  }

  /**
   * Verifica se o usuário é administrador
   * @returns boolean indicando se o usuário é admin
   */
  isAdmin(): boolean {
    const user = this.getUser();
    return user ? user.IsUsuarioAdmin : false;
  }

  /**
   * Verifica se o usuário é da Valor Saúde
   * @returns boolean indicando se o usuário é da Valor Saúde
   */
  isValorSaude(): boolean {
    const user = this.getUser();
    return user ? user.IsUsuarioValorSaude : false;
  }

  /**
   * Verifica se o usuário é de uma empresa
   * @returns boolean indicando se o usuário é de uma empresa
   */
  isCompanyUser(): boolean {
    const user = this.getUser();
    return user ? user.IsUsuarioEmpresa : false;
  }

  /**
   * Verifica se o usuário é de uma empresa gestora
   * @returns boolean indicando se o usuário é de uma empresa gestora
   */
  isCompanyManager(): boolean {
    const user = this.getUser();
    return user ? user.IsUsuarioEmpresaGestora : false;
  }

  /**
   * Retorna o perfil do usuário baseado em suas permissões
   * @returns string com o perfil do usuário
   */
  getUserProfile(): string {
    const user = this.getUser();
    if (!user) return '';

    
    if (user.IsUsuarioAdmin) {
      return 'Gestao';
    } else if (user.IsUsuarioValorSaude) {
      return 'Gestao';
    } else if (user.IsUsuarioEmpresaGestora) {
      return 'Operadora';
    } else if (user.IsUsuarioEmpresa) {
      return 'Empresa';
    } else if (user.IsUsuarioCliente) {
      return 'Cliente';
    }

    return 'Usuario';
  }

  /**
   * Retorna o token do usuário
   * @returns string com o token do usuário ou null se não existir
   */
  getToken(): string | null {
    const user = this.getUserData();
    return user ? user.token : "";
  }

  /**
   * Realiza o logout do usuário
   * Limpa os dados do localStorage e redireciona para a página de login
   */
  logout(): void {
    try {
      this.clearUserData();
      this.router.navigate(['/site']);
    } catch (error) {
      console.error('Erro ao realizar logout:', error);
    }
  }
} 